import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jobcost-monorepo/i18n';
import {
  API,
  AUTH,
  BASE,
  FORGET_PASSWORD,
  ROUTES,
  V1,
  cn,
  useBreakpoints,
} from '@jobcost-monorepo/utils';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button, LogoImage } from '../../../atoms';
import InputForm from '../../InputForm';
import { schema } from './validation';

type IFormInput = {
  email: string;
};

const ForgotPassword: React.FC = () => {
  const methods = useForm<IFormInput>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const { isSmall } = useBreakpoints();
  const email = methods.watch('email');

  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    methods.clearErrors();
  }, [email]);

  const handleSave = async () => {
    setIsLoading(true);
    const { email } = methods.getValues();

    const response = await fetch(
      `${BASE}${API}${V1}${AUTH}${FORGET_PASSWORD}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
      },
    );

    if (response.ok) {
      setIsEmailSent(true);
    }

    setIsLoading(false);
  };

  return (
    <FormProvider {...methods}>
      <div
        className="flex justify-center items-center w-screen h-screen overflow-hidden"
        style={{
          background:
            'linear-gradient(299.92deg, #514689 18.26%, #EF426F 117.31%)',
        }}
      >
        <div
          className={cn(
            'flex flex-col bg-white justify-center w-96 items-center rounded-[2rem] p-10',
            isSmall && 'mx-7',
          )}
        >
          <div className="flex justify-center items-center">
            <LogoImage className="mx-auto h-12 w-auto mb-8 fill-black dark:fill-white" />
          </div>

          <div className="w-full border-t border-primary2 mt-4" />

          {isEmailSent ? (
            <div className="flex w-full justify-center items-center mt-5">
              <p className="text-main2 text-1.6rem">
                {t('forgotPasswordPage.emailSent', { email })}
              </p>
            </div>
          ) : (
            <>
              <div className="block w-full mt-5">
                <InputForm
                  label={t('forgotPasswordPage.email')}
                  id="email"
                  name="email"
                  placeholder={t('forgotPasswordPage.insertEmail')}
                  errorName="email"
                />
              </div>

              <Button
                type="submit"
                onClick={methods.handleSubmit(handleSave)}
                className="mt-4 w-full"
                loading={isLoading}
              >
                {t('forgotPasswordPage.recoverPassword')}
              </Button>
            </>
          )}

          <p
            className="mt-4 text-1.6rem cursor-pointer text-purple hover:opacity-80"
            onClick={() => navigate(`/${ROUTES.SIGN_IN}`)}
          >
            {t('forgotPasswordPage.backToLogin')}
          </p>
        </div>
      </div>
    </FormProvider>
  );
};

export default ForgotPassword;
